import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Megaphone, Plus, Edit2, Trash2, Send, Eye, EyeOff,
    AlertCircle, Info, Sparkles, AlertTriangle, Wrench,
    Calendar, Clock, Users, Mail, BarChart3, CheckCircle,
    X, Save, Loader2
} from 'lucide-react';
import { apiMethods } from '@/services/api';

const AdminAnnouncements = () => {
    const queryClient = useQueryClient();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [viewingAnnouncement, setViewingAnnouncement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilter, setActiveFilter] = useState('all');

    // Date formatting helper
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Fetch announcements
    const { data, isLoading } = useQuery({
        queryKey: ['adminAnnouncements', currentPage, activeFilter],
        queryFn: async () => {
            const params = {
                page: currentPage,
                limit: 10
            };
            if (activeFilter !== 'all') {
                params.active = activeFilter === 'active';
            }
            const response = await apiMethods.announcements.getAll(params);
            return response.data;
        }
    });

    // Create announcement mutation
    const createMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.announcements.create(data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminAnnouncements']);
            setShowCreateModal(false);
        }
    });

    // Update announcement mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await apiMethods.announcements.update(id, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminAnnouncements']);
            setEditingAnnouncement(null);
        }
    });

    // Delete announcement mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const response = await apiMethods.announcements.delete(id);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminAnnouncements']);
        }
    });

    // Send email mutation
    const sendEmailMutation = useMutation({
        mutationFn: async (id) => {
            const response = await apiMethods.announcements.sendEmail(id);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['adminAnnouncements']);
        }
    });

    const typeIcons = {
        info: <Info className="h-5 w-5" />,
        feature: <Sparkles className="h-5 w-5" />,
        warning: <AlertTriangle className="h-5 w-5" />,
        maintenance: <Wrench className="h-5 w-5" />
    };

    const typeColors = {
        info: 'bg-blue-100 text-blue-800',
        feature: 'bg-purple-100 text-purple-800',
        warning: 'bg-yellow-100 text-yellow-800',
        maintenance: 'bg-gray-100 text-gray-800'
    };

    const priorityColors = {
        low: 'bg-green-100 text-green-800',
        medium: 'bg-yellow-100 text-yellow-800',
        high: 'bg-red-100 text-red-800'
    };

    const AnnouncementForm = ({ announcement, onSubmit, onCancel }) => {
        const [formData, setFormData] = useState({
            title: announcement?.title || '',
            content: announcement?.content || '',
            type: announcement?.type || 'info',
            priority: announcement?.priority || 'medium',
            showOnDashboard: announcement?.showOnDashboard ?? true,
            publishAt: announcement?.publishAt ? new Date(announcement.publishAt).toISOString().slice(0, 16) : '',
            expiresAt: announcement?.expiresAt ? new Date(announcement.expiresAt).toISOString().slice(0, 16) : ''
        });

        const handleSubmit = () => {
            if (!formData.title || !formData.content) {
                alert('Title and content are required');
                return;
            }
            onSubmit(formData);
        };

        return (
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        required
                        maxLength={200}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Content</label>
                    <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={4}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        required
                        maxLength={2000}
                    />
                    <p className="mt-1 text-xs text-gray-500">{formData.content.length}/2000 characters</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="info">Information</option>
                            <option value="feature">New Feature</option>
                            <option value="warning">Warning</option>
                            <option value="maintenance">Maintenance</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Priority</label>
                        <select
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={formData.showOnDashboard}
                            onChange={(e) => setFormData({ ...formData, showOnDashboard: e.target.checked })}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Show on user dashboard</span>
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Publish At</label>
                        <input
                            type="datetime-local"
                            value={formData.publishAt}
                            onChange={(e) => setFormData({ ...formData, publishAt: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Expires At (Optional)</label>
                        <input
                            type="datetime-local"
                            value={formData.expiresAt}
                            onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                    >
                        {announcement ? 'Update' : 'Create'} Announcement
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                                <Megaphone className="h-8 w-8 mr-3 text-primary-600" />
                                Announcements
                            </h1>
                            <p className="mt-2 text-gray-600">
                                Create and manage system-wide announcements for users
                            </p>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            New Announcement
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-6 flex items-center space-x-4">
                    <button
                        onClick={() => setActiveFilter('all')}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                            activeFilter === 'all'
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setActiveFilter('active')}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                            activeFilter === 'active'
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        Active
                    </button>
                    <button
                        onClick={() => setActiveFilter('inactive')}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                            activeFilter === 'inactive'
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                        Inactive
                    </button>
                </div>

                {/* Announcements List */}
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
                    </div>
                ) : data?.announcements?.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements yet</h3>
                        <p className="text-gray-500">Create your first announcement to keep users informed.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {data?.announcements?.map((announcement) => (
                            <div key={announcement._id} className="bg-white rounded-lg shadow">
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {announcement.title}
                                                </h3>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[announcement.type]}`}>
                                                    {typeIcons[announcement.type]}
                                                    <span className="ml-1">{announcement.type}</span>
                                                </span>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[announcement.priority]}`}>
                                                    {announcement.priority}
                                                </span>
                                                {announcement.effectivelyActive ? (
                                                    <Eye className="h-4 w-4 text-green-600" />
                                                ) : announcement.isExpired ? (
                                                    <Clock className="h-4 w-4 text-red-500" title="Expired" />
                                                ) : (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                )}
                                            </div>
                                            <p className="mt-2 text-gray-600 line-clamp-2">{announcement.content}</p>

                                            <div className="mt-4 flex items-center space-x-6 text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <Calendar className="h-4 w-4 mr-1" />
                                                    Published: {formatDate(announcement.publishAt)}
                                                </div>
                                                {announcement.expiresAt && (
                                                    <div className={`flex items-center ${announcement.isExpired ? 'text-red-600' : ''}`}>
                                                        <Clock className="h-4 w-4 mr-1" />
                                                        {announcement.isExpired ? 'Expired' : 'Expires'}: {formatDate(announcement.expiresAt)}
                                                    </div>
                                                )}
                                            </div>

                                            {announcement.statistics && (
                                                <div className="mt-3 flex items-center space-x-6 text-sm">
                                                    <div className="flex items-center text-gray-600">
                                                        <Users className="h-4 w-4 mr-1" />
                                                        Seen by {announcement.statistics.seenCount} of {announcement.statistics.totalUsers} users
                                                        ({announcement.statistics.seenPercentage}%)
                                                    </div>
                                                    {announcement.emailSent && (
                                                        <div className="flex items-center text-green-600">
                                                            <Mail className="h-4 w-4 mr-1" />
                                                            Email sent to {announcement.statistics.emailSentTo} users
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => setViewingAnnouncement(announcement)}
                                                className="text-gray-600 hover:text-gray-900"
                                                title="View Details"
                                            >
                                                <BarChart3 className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => setEditingAnnouncement(announcement)}
                                                className="text-gray-600 hover:text-gray-900"
                                                title="Edit"
                                            >
                                                <Edit2 className="h-5 w-5" />
                                            </button>
                                            {!announcement.emailSent && (
                                                <button
                                                    onClick={() => {
                                                        if (confirm('Send this announcement via email to all active users?')) {
                                                            sendEmailMutation.mutate(announcement._id);
                                                        }
                                                    }}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Send Email"
                                                    disabled={sendEmailMutation.isPending}
                                                >
                                                    {sendEmailMutation.isPending ? (
                                                        <Loader2 className="animate-spin h-5 w-5" />
                                                    ) : (
                                                        <Send className="h-5 w-5" />
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    if (confirm('Are you sure you want to delete this announcement?')) {
                                                        deleteMutation.mutate(announcement._id);
                                                    }
                                                }}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {data?.pagination && data.pagination.pages > 1 && (
                    <div className="mt-6 flex justify-center">
                        <nav className="flex space-x-2">
                            {Array.from({ length: data.pagination.pages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                                        currentPage === page
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </nav>
                    </div>
                )}

                {/* Create Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-900">Create Announcement</h2>
                            </div>
                            <div className="p-6">
                                <AnnouncementForm
                                    onSubmit={(data) => createMutation.mutate(data)}
                                    onCancel={() => setShowCreateModal(false)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {editingAnnouncement && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-900">Edit Announcement</h2>
                            </div>
                            <div className="p-6">
                                <AnnouncementForm
                                    announcement={editingAnnouncement}
                                    onSubmit={(data) => updateMutation.mutate({ id: editingAnnouncement._id, data })}
                                    onCancel={() => setEditingAnnouncement(null)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* View Details Modal */}
                {viewingAnnouncement && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-gray-900">Announcement Details</h2>
                                    <button
                                        onClick={() => setViewingAnnouncement(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{viewingAnnouncement.title}</h3>
                                    <div className="flex items-center space-x-3 mb-4">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${typeColors[viewingAnnouncement.type]}`}>
                                            {typeIcons[viewingAnnouncement.type]}
                                            <span className="ml-1">{viewingAnnouncement.type}</span>
                                        </span>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${priorityColors[viewingAnnouncement.priority]}`}>
                                            {viewingAnnouncement.priority}
                                        </span>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-md">
                                        <p className="text-gray-700 whitespace-pre-wrap">{viewingAnnouncement.content}</p>
                                    </div>
                                </div>

                                {viewingAnnouncement.statistics && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-blue-50 p-4 rounded-lg">
                                            <h4 className="text-sm font-medium text-blue-900 mb-2">View Statistics</h4>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-blue-700">Total Users</span>
                                                    <span className="text-sm font-semibold text-blue-900">
                                                        {viewingAnnouncement.statistics.totalUsers}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-blue-700">Seen By</span>
                                                    <span className="text-sm font-semibold text-blue-900">
                                                        {viewingAnnouncement.statistics.seenCount} ({viewingAnnouncement.statistics.seenPercentage}%)
                                                    </span>
                                                </div>
                                                <div className="mt-2">
                                                    <div className="w-full bg-blue-200 rounded-full h-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full"
                                                            style={{ width: `${viewingAnnouncement.statistics.seenPercentage}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-green-50 p-4 rounded-lg">
                                            <h4 className="text-sm font-medium text-green-900 mb-2">Email Statistics</h4>
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-green-700">Email Sent</span>
                                                    <span className="text-sm font-semibold text-green-900">
                                                        {viewingAnnouncement.emailSent ? 'Yes' : 'No'}
                                                    </span>
                                                </div>
                                                {viewingAnnouncement.emailSent && (
                                                    <>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-green-700">Sent To</span>
                                                            <span className="text-sm font-semibold text-green-900">
                                                                {viewingAnnouncement.statistics.emailSentTo} users
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-green-700">Sent At</span>
                                                            <span className="text-sm font-semibold text-green-900">
                                                                {formatDateTime(viewingAnnouncement.emailSentAt)}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-6 text-sm text-gray-500">
                                    <p>Created by {viewingAnnouncement.createdBy?.name} on {formatDateTime(viewingAnnouncement.createdAt)}</p>
                                    {viewingAnnouncement.updatedBy && (
                                        <p>Last updated by {viewingAnnouncement.updatedBy?.name} on {formatDateTime(viewingAnnouncement.updatedAt)}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminAnnouncements;